import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import { useLocalStorage } from 'react-use'
import { formatUnits, parseUnits } from 'viem'

import {
  Message,
  NetworkDisplay,
  NumberInput,
  TokenSelector,
} from '~/components/component-library'
import { MultiSignSwapMessage } from '~/components/forms/swap/fields/MultiSignSwapMessage'
import {
  useAutoSwapSelection,
  useSelectedSwapToken,
} from '~/components/forms/swap/hooks/useSwapHelpers'
import { PaymentSourceField } from '~/components/forms/transfer/fields/PaymentSource'
import {
  computeOfframpFee,
  useBankAccountMatch,
} from '~/components/forms/transfer/hooks/useOfframpDisplayData'
import { useTransferQuote } from '~/components/forms/transfer/hooks/useTransferQuote'
import { RecipientInput } from '~/components/forms/transfer/single/RecipientInput'
import { useEarnMaxOverride } from '~/components/forms/transfer/single/useEarnMaxOverride'
import {
  PersistedSelectedToken,
  TransferFormValues,
} from '~/components/forms/transfer/transferSchema'
import { useCompliantPaymentValidation } from '~/components/smart-accounts/useCompliantPaymentValidation'
import { formatOfframpFeeFooter } from '~/components/txn-display/state-change/utils/buildStateChangeList'
import {
  BRIDGE_FEE_PERCENT,
  getBridgeTokenType,
  MINIMUM_OFFRAMP_AMOUNT,
} from '~/constants/addresses'
import { SupportedChainId, SupportedVmTypes } from '~/constants/chains'
import { usePayeeLookupByAddress } from '~/hooks/compliance/usePayeeLookup'
import { useActiveOrg } from '~/hooks/org'
import { useSmartAccountName, useSmartAccounts } from '~/hooks/useSmartAccounts'
import { TokenMetadata, useTokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress, VmType } from '~/types'
import { Address, EvmAddress } from '~/utils/address'
import { calculateOfframpGrossAmount } from '~/utils/offramp'
import { isSameToken } from '~/utils/tokens'

export const SingleRecipientSection = ({
  sender,
  isOfframpVariant,
  defaultRecipient,
  lockRecipient,
  isPrivate,
  tokenFilter,
  chainFilter,
  hasSendRuleRestrictions,
  onRecipientSelect,
}: {
  sender: IAddress
  isOfframpVariant?: boolean
  defaultRecipient?: IAddress
  lockRecipient: boolean
  isPrivate: boolean
  tokenFilter: (token: {
    chainId: SupportedChainId
    address: string
  }) => boolean
  chainFilter: (chainId: SupportedChainId) => boolean
  hasSendRuleRestrictions: boolean
  onRecipientSelect?: (address: IAddress) => void
}) => {
  const methods = useFormContext<TransferFormValues>()

  const { control, setValue, trigger, clearErrors } = methods

  const transferToken = useWatch({ control, name: 'transferToken' })
  const recipients = useWatch({ control, name: 'recipients' })
  const swapSellToken = useWatch({ control, name: 'swapData.sellToken.token' })

  const receiver = recipients[0]?.receiver?.address

  // The recipient↔token VM-compatibility check lives in the resolver; RHF only
  // re-validates the field that changed, so re-run the recipient-address path
  // when the token or recipient changes (including a clear, which sets the value
  // without re-validating).
  useEffect(() => {
    trigger('recipients.0.receiver.address')
  }, [transferToken?.token, swapSellToken, receiver, trigger])

  const isCompliantPayment =
    !!recipients[0]?.receiver?.compliantPaymentRelationId

  const transferTokenMetadata = useTokenMetadata({
    address: transferToken?.token,
    chainId: transferToken?.chainId,
    includePrice: true,
  })

  const hasTokenPrice = !!transferTokenMetadata?.price

  const { smartAccounts } = useSmartAccounts()

  const senderAccount = smartAccounts.find(
    (account) => account.address === sender,
  )

  const { org } = useActiveOrg()
  const [defaultSelectedToken, setDefaultSelectedToken] = useLocalStorage<
    PersistedSelectedToken | undefined
  >(`splits.defaultSelectedToken.${org?.id}`, undefined)

  useCompliantPaymentValidation()

  const { onSelectAutoSwap } = useAutoSwapSelection()
  const selectedToken = useSelectedSwapToken()

  const { isDirectionReversed, quoteError } = useTransferQuote({ isPrivate })

  // Private quotes skip the direct Earn withdrawal route, so private sends keep
  // the liquid MAX. The Offramp dialog's locked token input shows no MAX.
  const earnMaxOverride = useEarnMaxOverride({
    sender,
    token: transferToken,
    enabled: !isPrivate && !isOfframpVariant,
  })

  useEffect(() => {
    const totalAmount = recipients.reduce((acc, recipient) => {
      return (
        acc +
        parseUnits(
          recipient.amount ?? '0',
          transferTokenMetadata?.decimals ?? 18,
        )
      )
    }, BigInt(0))

    setValue(
      'transferToken.amount',
      formatUnits(totalAmount, transferTokenMetadata?.decimals ?? 18),
    )
    // The amount input never blurs through RHF, so resolver checks on the
    // amount (the gas top-up minimum) would only surface on submit. A fresh
    // form has no recipient row yet; validating that undefined amount would
    // surface zod's "expected string" error before the user types anything.
    if (recipients[0]?.amount !== undefined) trigger('recipients.0.amount')
  }, [recipients, setValue, trigger, transferTokenMetadata?.decimals])

  const selectedTokenMetadata: TokenMetadata | undefined = useTokenMetadata({
    address: selectedToken?.address,
    chainId: selectedToken?.chainId,
  })

  const resetForm = useCallback(() => {
    setValue('calls', [])
    setValue('swapData.earnToken', undefined)
    setValue('swapData.sellToken', undefined)
  }, [setValue])

  // Everything a bank recipient leaves behind: the fiat typed for it, the
  // grossed-up token amount, any swap into its settlement token, and the
  // minimum error. That error has to go explicitly: setValue never
  // validates, and nothing revalidates an empty field.
  const clearOfframpEntry = useCallback(() => {
    setValue('offrampRecipientAmount', '')
    setValue('recipients.0.amount', '')
    clearErrors('offrampRecipientAmount')
    resetForm()
  }, [setValue, clearErrors, resetForm])

  useEffect(() => {
    if (
      isOfframpVariant ||
      transferToken?.token ||
      !defaultSelectedToken ||
      !chainFilter(defaultSelectedToken.chainId) ||
      !tokenFilter({
        chainId: defaultSelectedToken.chainId,
        address: defaultSelectedToken.token,
      })
    ) {
      return
    }

    setValue('transferToken.token', defaultSelectedToken.token)
    setValue('transferToken.chainId', defaultSelectedToken.chainId)
  }, [
    chainFilter,
    defaultSelectedToken,
    isOfframpVariant,
    setValue,
    tokenFilter,
    transferToken?.token,
  ])

  const bankAccountMatch = useBankAccountMatch(receiver)

  // If it's an external bank account, set the chain & token. Amount is the only
  // option the user can actually set
  const previousBankAccountIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (receiver && bankAccountMatch) {
      // Address.equal, not !==: the form holds the checksummed form, so a raw
      // compare against the API value re-fires this on every re-render
      const shouldUpdate =
        transferToken?.chainId !== bankAccountMatch.liquidationChainId ||
        !transferToken?.token ||
        !Address.equal(transferToken.token, bankAccountMatch.liquidationToken)
      if (shouldUpdate) {
        setValue('transferToken.chainId', bankAccountMatch.liquidationChainId)
        setValue(
          'transferToken.token',
          EvmAddress.format(bankAccountMatch.liquidationToken),
        )
      }
      // Selecting a bank starts the amount fresh (it was denominated in the
      // old token). Keyed on account id so org refetches can't re-clear.
      if (
        previousBankAccountIdRef.current !== bankAccountMatch.externalAccountId
      ) {
        previousBankAccountIdRef.current = bankAccountMatch.externalAccountId
        // The settlement token changes with the bank, so any source picked to
        // swap into the old one is meaningless — and a stale one leaves the
        // form waiting on a quote that will never come.
        clearOfframpEntry()
      }
    } else {
      // Leaving a bank: the amount was denominated in fiat and grossed up for
      // the fee, so it means nothing to whoever comes next.
      if (previousBankAccountIdRef.current !== undefined) {
        clearOfframpEntry()
      }
      previousBankAccountIdRef.current = undefined
    }
  }, [
    bankAccountMatch,
    setValue,
    clearOfframpEntry,
    receiver,
    transferToken?.chainId,
    transferToken?.token,
  ])

  const showOfframpAmountInput = !!bankAccountMatch && !isOfframpVariant
  // The user types the fiat the bank receives; the grossed-up token amount
  // (what actually leaves the account) lands in recipients.0.amount so every
  // downstream consumer keeps reading the amount sent. Validated on write:
  // the field is derived, so nothing else refreshes its error, and one left
  // behind by a cleared wallet amount would otherwise keep Review disabled.
  const onOfframpRecipientAmountChange = useCallback(
    (value?: string) => {
      setValue('recipients.0.amount', toOfframpGrossAmountInput(value), {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue],
  )
  const offrampAmount = Number(recipients[0]?.amount) || 0
  // transferToken only syncs to the bank's settlement token in an effect later
  const offrampCurrency =
    bankAccountMatch &&
    getBridgeTokenType(
      bankAccountMatch.liquidationChainId,
      Address.format(bankAccountMatch.liquidationToken),
    ) === 'eurc'
      ? 'EUR'
      : 'USD'
  const offrampCurrencySymbol = offrampCurrency === 'EUR' ? '€' : '$'
  // Zero fee renders without the parenthetical, so the disclaimer always shows
  const offrampFooter = `Minimum ${offrampCurrencySymbol}${MINIMUM_OFFRAMP_AMOUNT}. ${formatOfframpFeeFooter(
    {
      ...computeOfframpFee(offrampAmount),
      currency: offrampCurrency,
    },
  )}.`

  const showMultiSignSwapMessage = !!selectedToken

  // The always-visible Payment source input is functional once there's a from
  // account, a priced + valid destination token, an amount, and a recipient on
  // a compatible VM. Otherwise the field renders a disabled placeholder.
  const isPaymentSourceReady =
    !!sender &&
    hasTokenPrice &&
    !!transferToken?.token &&
    Address.validate(transferToken.token) &&
    parseFloat(transferToken?.amount ?? '0') > 0 &&
    !!receiver &&
    Address.validate(receiver) &&
    Address.compatible(transferToken.token, receiver) &&
    !!transferToken?.chainId

  const receiverVmTypes: VmType[] = useMemo(() => {
    if (!receiver || !Address.validate(receiver)) return [...SupportedVmTypes]
    const vms = Address.vmTypes(receiver)
    return vms.length > 0 ? vms : [...SupportedVmTypes]
  }, [receiver])

  const isSvmOnlyReceiver =
    receiverVmTypes.length === 1 && receiverVmTypes[0] === 'svm'

  const singleRecipientOnSelect = useCallback(
    (newRecipientAddress: IAddress) => {
      resetForm()
      onRecipientSelect?.(newRecipientAddress)
    },
    [resetForm, onRecipientSelect],
  )

  const onTokenSelect = useCallback(
    (token: { address: string; chainId: number }) => {
      setDefaultSelectedToken({
        token: token.address as IAddress,
        chainId: token.chainId as SupportedChainId,
      })
      // Changing to a different token resets the form; re-selecting the same one
      // leaves it intact.
      if (!isSameToken(transferToken, token)) {
        resetForm()
      }
    },
    [setDefaultSelectedToken, transferToken, resetForm],
  )

  return (
    <>
      <RecipientInput
        sender={sender}
        shouldAutoFocus={false}
        isOfframpVariant={isOfframpVariant}
        lockRecipient={lockRecipient}
        onSelect={singleRecipientOnSelect}
        defaultRecipient={defaultRecipient}
      />
      <div className="flex flex-col space-y-2">
        {showOfframpAmountInput ? (
          <NumberInput<TransferFormValues>
            control={control}
            inputName="offrampRecipientAmount"
            label="Recipient gets"
            prefix={offrampCurrencySymbol}
            decimalScale={2}
            placeholder={`${MINIMUM_OFFRAMP_AMOUNT.toFixed(2)} or more`}
            footer={offrampFooter}
            onChange={onOfframpRecipientAmountChange}
          />
        ) : (
          <TokenSelector<TransferFormValues>
            control={control}
            vmTypes={receiverVmTypes}
            addressInputName="transferToken.token"
            chainIdInputName="transferToken.chainId"
            amountInputName="recipients.0.amount"
            label={
              isOfframpVariant
                ? 'Amount'
                : isDirectionReversed
                  ? 'Recipient gets approximately'
                  : 'Recipient gets'
            }
            denomination={isOfframpVariant ? 'token' : 'switch'}
            filterAccountAddress={sender}
            showBalances
            sortBy="frequency"
            balanceOverride={earnMaxOverride}
            tokenFilter={(token) =>
              tokenFilter({
                chainId: token.chainId as SupportedChainId,
                address: token.address,
              })
            }
            // The private feature list and the contact send rules are strict
            // allowlists. Relay tokens must also pass the filter.
            strictTokenFilter={isPrivate || hasSendRuleRestrictions}
            // chainFilter only applies to EVM org chains (network tabs).
            // SVM tokens come through relay networks ("More" dropdown), so
            // we hide all EVM chain tabs when the recipient is SVM-only.
            chainFilter={(chainId) =>
              isSvmOnlyReceiver
                ? false
                : chainFilter(chainId as SupportedChainId)
            }
            onTokenSelect={onTokenSelect}
            disabled={!!bankAccountMatch || isOfframpVariant}
            disabledReason={
              isOfframpVariant || bankAccountMatch
                ? 'Unable to change offramp token'
                : undefined
            }
            // Send rules allow only org chains. A restricted recipient cannot
            // receive on a relay network.
            allowRelayNetworks={!isCompliantPayment && !hasSendRuleRestrictions}
          />
        )}
      </div>
      <PaymentSourceField
        ready={isPaymentSourceReady}
        sender={sender}
        destinationToken={transferToken}
        destinationTokenSymbol={transferTokenMetadata?.symbol}
        selectedToken={selectedToken}
        selectedTokenSymbol={selectedTokenMetadata?.symbol}
        onSelect={onSelectAutoSwap}
        isPrivate={isPrivate}
        quoteError={quoteError}
        disabledReason="Enter a recipient, token, and amount first"
        // Offramp (dedicated variant or a bank-account recipient): keep the
        // direct-pay row's settlement-token "amount available" line shown.
        isOfframp={isOfframpVariant || !!bankAccountMatch}
      />
      <CompliantPaymentMessage recipientAddress={receiver} />
      <InactiveNetworkWarning
        senderAddress={sender}
        recipientAddress={receiver}
        chainId={transferToken?.chainId}
      />
      {showMultiSignSwapMessage && (
        <MultiSignSwapMessage smartAccountId={senderAccount?.id} />
      )}
    </>
  )
}

// Empty or unparsable input clears the sent amount rather than sending 0
const toOfframpGrossAmountInput = (value?: string): string => {
  const net = Number(value)
  if (!value || Number.isNaN(net) || net <= 0) return ''
  return calculateOfframpGrossAmount(net, BRIDGE_FEE_PERCENT / 100).toFixed(2)
}

const CompliantPaymentMessage = ({
  recipientAddress,
}: {
  recipientAddress: IAddress | undefined
}) => {
  const { control } = useFormContext<TransferFormValues>()
  const { payee } = usePayeeLookupByAddress(recipientAddress)

  const isCompliantPayment = !!useWatch({
    control,
    name: 'recipients.0.receiver.compliantPaymentRelationId',
  })

  const isCompliant = payee?.payeeOrg?.isCompliant ?? false

  if (!isCompliantPayment) {
    return null
  }

  return (
    <Message
      title="Compliant payment"
      variant={isCompliant ? 'info' : 'warning'}
    >
      {isCompliant
        ? 'The recipient has been verified, and this payment will be included in relevant tax forms'
        : 'The recipient has not been verified yet. You can still send the payment and it will be included in relevant tax forms once they are verified.'}
    </Message>
  )
}

const InactiveNetworkWarning = ({
  senderAddress,
  recipientAddress,
  chainId,
}: {
  senderAddress: IAddress | undefined
  recipientAddress: IAddress | undefined
  chainId: SupportedChainId | undefined
}) => {
  const { org } = useActiveOrg()

  const senderName = useSmartAccountName(senderAddress)
  const recipientName = useSmartAccountName(recipientAddress)

  const orgName = org?.name

  // NetworkDisplay resolves the network name itself, so the gate no longer
  // waits on relay metadata (which never arrives for registry chains).
  const shouldShowWarning = useMemo(() => {
    if (
      !senderAddress ||
      !chainId ||
      !senderName ||
      !orgName ||
      !recipientName
    ) {
      return false
    }

    return !org?.smartAccountsChainIds?.includes(chainId)
  }, [
    senderAddress,
    chainId,
    senderName,
    org?.smartAccountsChainIds,
    orgName,
    recipientName,
  ])

  if (!shouldShowWarning) {
    return null
  }

  return (
    <Message variant="warning">
      <div>
        {orgName} is not active on{' '}
        <NetworkDisplay layout="inline-text" chainId={chainId!} />. You should
        not send funds to {recipientName} on this network, as they may not be
        recoverable.
      </div>
    </Message>
  )
}
